import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import ErrorAlert from "components/ErrorAlert";
import Table from "examples/Tables/Table";
import GoalButton from "layouts/goals/components/GoalButton";
import GoalActionButton from "layouts/goals/components/GoalActionButton";
import GoalPill from "layouts/goals/components/GoalPill";
import { fetchMe, switchOrganization } from "layouts/authentication/auth.thunk";
import { persistor } from "store/store";
import useOrganizations from "./useOrganizations";
import OrganizationFormModal from "./components/OrganizationFormModal";
import MoveTeamModal from "./components/MoveTeamModal";
import FeaturesModal from "./components/FeaturesModal";
import {
  ORGANIZATIONS_COLUMNS,
  ORGANIZATIONS_MESSAGES,
  FEATURES,
  TEAMS_COLUMNS,
  hasAllFeatures,
} from "./organizations.constants";

const muted = (text) => (
  <SuiTypography variant="caption" color="secondary">
    {text}
  </SuiTypography>
);

function Organizations() {
  const {
    auth: { admin, organization: viewing },
  } = useSelector((state) => state);
  const dispatch = useDispatch();

  const {
    organizations,
    teams,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createOrganization,
    updateOrganization,
    updateFeatures,
    moveTeam,
    showError,
    clearError,
    clearSuccess,
  } = useOrganizations(admin?.id);

  const [formTarget, setFormTarget] = useState(null); // { organization } ; null organization = new
  const [moving, setMoving] = useState(null);
  const [editingFeatures, setEditingFeatures] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [admin?.id]);

  // Switches to the organization: the whole portal then shows it the way its
  // own admins see it. Everything on screen belongs to the old one, so reload.
  const viewOrganization = async (organization) => {
    const result = await dispatch(
      switchOrganization({ adminId: admin.id, organizationId: organization.id })
    );
    if (switchOrganization.fulfilled.match(result)) {
      await persistor.flush();
      window.location.assign("/");
    } else {
      showError(`Failed to open ${organization.name}.`);
    }
  };

  const organizationName = (id) => organizations.find((item) => item.id === id)?.name || "—";

  const submit =
    (action, close) =>
    async (...args) => {
      setIsSubmitting(true);
      const ok = await action(...args);
      setIsSubmitting(false);
      if (ok) close();
    };
  const handleSave = submit(
    (body) =>
      formTarget.organization
        ? updateOrganization(formTarget.organization.id, body)
        : createOrganization(body),
    () => setFormTarget(null)
  );
  // The channel dropdown comes from the signed-in data, which only reloads on
  // sign-in or when switching organization, so reload it for the organization
  // being viewed (it just gained or lost this workspace).
  const handleMove = submit(
    async (teamId, organizationId) => {
      const ok = await moveTeam(teamId, organizationId);
      if (ok && viewing?.id) {
        await dispatch(switchOrganization({ adminId: admin.id, organizationId: viewing.id }));
      }
      return ok;
    },
    () => setMoving(null)
  );
  // The signed-in data holds the features of the organization being viewed (it
  // drives the menu and the role editor), so reload it when those change.
  const handleFeatures = submit(
    async (id, features) => {
      const ok = await updateFeatures(id, features);
      if (ok && id === viewing?.id) dispatch(fetchMe(admin.id));
      return ok;
    },
    () => setEditingFeatures(null)
  );

  const organizationRows = organizations.map((organization) => ({
    organization: (
      <SuiBox display="flex" flexDirection="column" px={1} py={0.5}>
        <SuiBox display="flex" alignItems="center" gap={1}>
          <SuiTypography variant="button" fontWeight="medium">
            {organization.name}
          </SuiTypography>
          {organization.id === viewing?.id && <GoalPill color="info">Viewing</GoalPill>}
        </SuiBox>
        {muted(organization.slug)}
      </SuiBox>
    ),
    workspaces: muted(organization.team_count),
    admins: muted(organization.admin_count),
    features: hasAllFeatures(organization.features) ? (
      <GoalPill color="success">All features</GoalPill>
    ) : (
      <GoalPill color="warning">
        {organization.features.length} of {FEATURES.length}
      </GoalPill>
    ),
    actions: (
      <SuiBox display="flex" gap={1} justifyContent="center">
        <GoalActionButton
          icon="visibility"
          tooltip={organization.id === viewing?.id ? "Currently viewing" : "View organization"}
          color="secondary"
          disabled={organization.id === viewing?.id}
          onClick={() => viewOrganization(organization)}
        />
        <GoalActionButton
          icon="edit"
          tooltip="Rename"
          color="info"
          onClick={() => setFormTarget({ organization })}
        />
        <GoalActionButton
          icon="tune"
          tooltip="Features"
          color="success"
          onClick={() => setEditingFeatures(organization)}
        />
      </SuiBox>
    ),
  }));

  const teamRows = teams.map((team) => ({
    workspace: (
      <SuiBox display="flex" flexDirection="column" px={1} py={0.5}>
        <SuiTypography variant="button" fontWeight="medium">
          {team.name}
        </SuiTypography>
        {muted(team.domain)}
      </SuiBox>
    ),
    organization: <GoalPill color="info">{organizationName(team.organization_id)}</GoalPill>,
    actions: (
      <SuiBox display="flex" justifyContent="center">
        <GoalActionButton
          icon="drive_file_move"
          tooltip="Move to another organization"
          color="warning"
          disabled={organizations.length < 2}
          onClick={() => setMoving(team)}
        />
      </SuiBox>
    ),
  }));

  const loading = (
    <SuiBox display="flex" justifyContent="center" py={4}>
      <CircularProgress color="dark" size={30} />
    </SuiBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox py={3}>
        {errorMessage && (
          <ErrorAlert color="error" message={errorMessage} clearError={clearError} />
        )}
        {successMessage && (
          <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
        )}
        <SuiBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          <SuiTypography variant="h5" fontWeight="medium">
            Organizations
          </SuiTypography>
          <GoalButton
            variant="primary"
            icon="add"
            onClick={() => setFormTarget({ organization: null })}
          >
            Add organization
          </GoalButton>
        </SuiBox>
        <Card>
          <SuiBox p={3}>
            {isLoading ? (
              loading
            ) : (
              <Table columns={ORGANIZATIONS_COLUMNS} rows={organizationRows} />
            )}
          </SuiBox>
        </Card>

        <SuiTypography variant="h6" fontWeight="medium" mt={4} mb={2}>
          Workspaces
        </SuiTypography>
        <Card>
          <SuiBox p={3}>
            {isLoading ? (
              loading
            ) : !isEmpty(teamRows) ? (
              <Table columns={TEAMS_COLUMNS} rows={teamRows} />
            ) : (
              muted(ORGANIZATIONS_MESSAGES.emptyTeams)
            )}
          </SuiBox>
        </Card>

        <OrganizationFormModal
          open={!!formTarget}
          onClose={() => setFormTarget(null)}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          organization={formTarget?.organization || null}
        />
        <FeaturesModal
          open={!!editingFeatures}
          onClose={() => setEditingFeatures(null)}
          onSave={handleFeatures}
          isSubmitting={isSubmitting}
          organization={editingFeatures}
        />
        <MoveTeamModal
          open={!!moving}
          onClose={() => setMoving(null)}
          onMove={handleMove}
          isSubmitting={isSubmitting}
          team={moving}
          organizations={organizations}
        />
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Organizations;
